import { createMemo, createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { createRoute } from "@tanstack/solid-router";
import { ArchiveRestore, Dumbbell, ListChecks, Trash2 } from "@fleet/icons";

import type { Exercise, Routine } from "@gymlog/powersync";
import {
  deleteExerciseMutation,
  deleteRoutineMutation,
  exerciseDisplayName,
  getTrashedExercisesQuery,
  getTrashedRoutinesQuery,
  queryKeys,
  restoreExerciseMutation,
  restoreRoutineMutation,
  TRASH_PURGE_DAYS,
} from "@gymlog/shared";
import { createPowerSyncQuery } from "@gymlog/shared/solid";

import { Card, CardContent, PageContainer, PageHeader } from "@fleet/ui";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { DumbbellIllustration } from "@/components/ui/illustrations";
import { appRoute } from "./route";
import { useGymCtx } from "@/features/gym/hooks/use-gym-ctx";
import { muscleGroupLabel } from "@/features/gym/lib/labels";

type PurgeTarget =
  | { kind: "routine"; id: string; name: string }
  | { kind: "exercise"; id: string; name: string };

/**
 * Trash (UNDO-TRASH-LIGHT L2): custom routines and exercises with soft-delete
 * (`deleted_at`).
 *
 * - Deleting from UI = move here. Set/session history of a
 *   deleted exercise is PRESERVED and reappears intact upon restore (same
 *   criterion as check-ins in habits): PRs/charts/summary are recomputed.
 * - "Restore" clears `deleted_at` (undoable: toast pill offers re-deleting).
 * - "Delete permanently" performs actual DELETE (cascades sets for
 *   exercises) after explicit confirmation — irreversible, WITHOUT undo.
 * - Normal views (catalog, routines, summary) exclude deleted items via
 *   `deleted_at IS NULL`: zero ad-hoc filters.
 */
function TrashPage() {
  const ctx = useGymCtx();
  const { t, locale } = ctx;

  const [confirmPurge, setConfirmPurge] = createSignal<PurgeTarget | null>(null);
  const [submitting, setSubmitting] = createSignal(false);

  const trashedRoutines = createPowerSyncQuery<Routine>({
    queryKey: () => queryKeys.gym.trashedRoutines(ctx.userId()),
    db: ctx.db,
    queryFn: () => {
      const db = ctx.drizzle();
      const uid = ctx.userId();
      return db && uid ? getTrashedRoutinesQuery(db, uid) : null;
    },
    enabled: () => Boolean(ctx.drizzle()) && Boolean(ctx.userId()),
  });

  const trashedExercises = createPowerSyncQuery<Exercise>({
    queryKey: () => queryKeys.gym.trashedExercises(ctx.userId()),
    db: ctx.db,
    queryFn: () => {
      const db = ctx.drizzle();
      const uid = ctx.userId();
      return db && uid ? getTrashedExercisesQuery(db, uid) : null;
    },
    enabled: () => Boolean(ctx.drizzle()) && Boolean(ctx.userId()),
  });

  const isEmpty = createMemo(
    () => (trashedRoutines.data ?? []).length === 0 && (trashedExercises.data ?? []).length === 0,
  );

  const deletedLabel = (iso: string | null) => {
    if (!iso) {
      return "";
    }
    const date = new Date(iso);
    return Number.isNaN(date.getTime())
      ? ""
      : new Intl.DateTimeFormat(locale(), {
          dateStyle: "medium",
          timeStyle: "short",
        }).format(date);
  };

  const MS_PER_DAY = 86_400_000;

  /**
   * Purge countdown (SOFT_DELETE_STANDARD): "will be deleted
   * permanently in N days" = deleted_at + TRASH_PURGE_DAYS − now. Returns
   * remaining days (minimum 0) or null if job is disabled
   * (TRASH_PURGE_DAYS <= 0) or date is invalid.
   */
  const daysUntilPurge = (iso: string | null): number | null => {
    if (TRASH_PURGE_DAYS <= 0 || !iso) {
      return null;
    }
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) {
      return null;
    }
    const purgeAt = date.getTime() + TRASH_PURGE_DAYS * MS_PER_DAY;
    return Math.max(0, Math.ceil((purgeAt - Date.now()) / MS_PER_DAY));
  };

  const restoreRoutine = async (id: string) => {
    const db = ctx.drizzle();
    if (!db || submitting()) {
      return;
    }
    setSubmitting(true);
    try {
      await restoreRoutineMutation(db, id);
    } finally {
      setSubmitting(false);
    }
  };

  const restoreExercise = async (id: string) => {
    const db = ctx.drizzle();
    const uid = ctx.userId();
    if (!db || !uid || submitting()) {
      return;
    }
    setSubmitting(true);
    try {
      await restoreExerciseMutation(db, uid, id);
    } finally {
      setSubmitting(false);
    }
  };

  const purge = async (target: PurgeTarget) => {
    const db = ctx.drizzle();
    const uid = ctx.userId();
    if (!db || !uid) {
      return;
    }
    if (target.kind === "routine") {
      await deleteRoutineMutation(db, target.id);
    } else {
      await deleteExerciseMutation(db, uid, target.id);
    }
  };

  return (
    <PageContainer maxWidth="2xl" class="space-y-6" containerClass="scrollbar-thin">
      <div data-testid="trash-view">
        <PageHeader
          title={t(msg`Trash`)}
          description={t(
            msg`Deleted routines and exercises keep their history and can be restored anytime.`,
          )}
          backTo="/profile"
          icon={Trash2}
        />
      </div>

      <Show
        when={!isEmpty()}
        fallback={
          <div data-testid="trash-empty">
            <EmptyState
              icon={Trash2}
              illustration={DumbbellIllustration}
              title={t(msg`Trash is empty`)}
              description={t(
                msg`Routines and exercises you delete land here — nothing is lost right away.`,
              )}
            />
          </div>
        }
      >
        {/* Routines */}
        <Show when={(trashedRoutines.data ?? []).length > 0}>
          <section class="space-y-2">
            <h2 class="flex items-center gap-1.5 text-sm font-bold uppercase tracking-wider text-muted-foreground">
              <ListChecks class="h-4 w-4" />
              {t(msg`Routines`)}
            </h2>
            <div class="flex flex-col gap-2">
              <For each={trashedRoutines.data ?? []}>
                {(routine) => (
                  <Card data-testid="trash-row" data-trash-name={routine.name ?? ""}>
                    <CardContent class="flex items-center gap-3 p-3">
                      <span class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                        <ListChecks class="h-5 w-5" />
                      </span>
                      <div class="min-w-0 flex-1">
                        <p class="truncate text-sm font-medium">{routine.name}</p>
                        <p class="mt-0.5 text-xs text-muted-foreground">
                          {t(msg`Deleted {date}`, {
                            date: deletedLabel(routine.deleted_at),
                          })}
                        </p>
                        <Show when={daysUntilPurge(routine.deleted_at) !== null}>
                          <p
                            class="mt-0.5 text-xs text-muted-foreground/80"
                            data-testid="trash-purge-countdown"
                          >
                            {t(msg`Will be permanently deleted in {days} days`, {
                              days: daysUntilPurge(routine.deleted_at) ?? 0,
                            })}
                          </p>
                        </Show>
                      </div>
                      <div class="flex shrink-0 items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid="trash-restore"
                          disabled={!ctx.drizzle() || submitting()}
                          onClick={() => void restoreRoutine(routine.id)}
                        >
                          <ArchiveRestore class="mr-1 h-4 w-4" />
                          {t(msg`Restore`)}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          class="text-destructive hover:text-destructive"
                          data-testid="trash-delete-forever"
                          disabled={!ctx.drizzle() || submitting()}
                          onClick={() =>
                            setConfirmPurge({
                              kind: "routine",
                              id: routine.id,
                              name: routine.name ?? "",
                            })
                          }
                        >
                          <Trash2 class="mr-1 h-4 w-4" />
                          {t(msg`Delete forever`)}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </For>
            </div>
          </section>
        </Show>

        {/* Custom exercises */}
        <Show when={(trashedExercises.data ?? []).length > 0}>
          <section class="space-y-2">
            <h2 class="flex items-center gap-1.5 text-sm font-bold uppercase tracking-wider text-muted-foreground">
              <Dumbbell class="h-4 w-4" />
              {t(msg`Exercises`)}
            </h2>
            <div class="flex flex-col gap-2">
              <For each={trashedExercises.data ?? []}>
                {(exercise) => (
                  <Card
                    data-testid="trash-row"
                    data-trash-name={exerciseDisplayName(exercise, locale())}
                  >
                    <CardContent class="flex items-center gap-3 p-3">
                      <span class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                        <Dumbbell class="h-5 w-5" />
                      </span>
                      <div class="min-w-0 flex-1">
                        <p class="truncate text-sm font-medium">
                          {exerciseDisplayName(exercise, locale())}
                        </p>
                        <p class="mt-0.5 text-xs text-muted-foreground">
                          {t(muscleGroupLabel(exercise.muscle_group))} ·{" "}
                          {t(msg`Deleted {date}`, {
                            date: deletedLabel(exercise.deleted_at),
                          })}
                        </p>
                        <Show when={daysUntilPurge(exercise.deleted_at) !== null}>
                          <p
                            class="mt-0.5 text-xs text-muted-foreground/80"
                            data-testid="trash-purge-countdown"
                          >
                            {t(msg`Will be permanently deleted in {days} days`, {
                              days: daysUntilPurge(exercise.deleted_at) ?? 0,
                            })}
                          </p>
                        </Show>
                      </div>
                      <div class="flex shrink-0 items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid="trash-restore"
                          disabled={!ctx.drizzle() || submitting()}
                          onClick={() => void restoreExercise(exercise.id)}
                        >
                          <ArchiveRestore class="mr-1 h-4 w-4" />
                          {t(msg`Restore`)}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          class="text-destructive hover:text-destructive"
                          data-testid="trash-delete-forever"
                          disabled={!ctx.drizzle() || submitting()}
                          onClick={() =>
                            setConfirmPurge({
                              kind: "exercise",
                              id: exercise.id,
                              name: exerciseDisplayName(exercise, locale()),
                            })
                          }
                        >
                          <Trash2 class="mr-1 h-4 w-4" />
                          {t(msg`Delete forever`)}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </For>
            </div>
          </section>
        </Show>
      </Show>

      <ConfirmDialog
        open={confirmPurge() !== null}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmPurge(null);
          }
        }}
        title={t(msg`Delete forever?`)}
        description={t(
          msg`This permanently deletes it and its whole history. This cannot be undone.`,
        )}
        confirmLabel={t(msg`Delete forever`)}
        variant="destructive"
        onConfirm={() => {
          const target = confirmPurge();
          setConfirmPurge(null);
          if (target) {
            void purge(target);
          }
        }}
      />
    </PageContainer>
  );
}

export const profileTrashRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/profile/trash",
  component: TrashPage,
});
